import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVehicleImage } from "@/hooks/useVehicleImage";
import defaultVehicleImage from "@/assets/xxx.png";

interface VehicleImagePreviewProps {
  make?: string;
  model?: string;
  compact?: boolean;
  variant?: "default" | "thumbnail";
  /** When true with thumbnail, renders make/model under the image inside the card. */
  showLabel?: boolean;
  className?: string;
}

export function VehicleImagePreview({
  make,
  model,
  compact = false,
  variant = "default",
  showLabel = false,
  className,
}: VehicleImagePreviewProps) {
  const { data: imageUrl, isLoading, isFetching } = useVehicleImage(make, model);

  if (!make?.trim() || !model?.trim()) return null;

  const isResolving = isLoading || isFetching;
  const label = `${make} ${model}`;
  const displaySrc = imageUrl ?? defaultVehicleImage;

  if (variant === "thumbnail") {
    return (
      <div
        className={cn(
          "relative shrink-0 overflow-hidden rounded-xl border border-border/60 bg-muted/30 shadow-sm",
          showLabel
            ? "flex w-[5.5rem] flex-col sm:w-28"
            : "h-[4.5rem] w-[5.5rem] sm:h-24 sm:w-28",
          className
        )}
        title={label}
      >
        <div
          className={cn(
            "relative flex items-center justify-center",
            showLabel ? "h-[4.5rem] w-full sm:h-24" : "h-full w-full"
          )}
        >
          {isResolving ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <img
              src={displaySrc}
              alt={label}
              className="h-full w-full object-contain p-1.5"
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
        {showLabel ? (
          <p className="border-t border-border/50 px-1.5 py-1.5 text-center text-[10px] font-semibold leading-tight text-foreground sm:text-[11px]">
            {label}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-muted/60 via-background to-primary/5",
        compact ? "p-3" : "p-4",
        className
      )}
    >
      <div
        className={cn(
          "relative flex items-center justify-center overflow-hidden rounded-xl bg-muted/40",
          compact ? "h-28" : "h-40 sm:h-44"
        )}
      >
        {isResolving ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <span className="text-xs font-medium">Loading vehicle...</span>
          </div>
        ) : (
          <img
            src={displaySrc}
            alt={label}
            className="h-full w-full object-contain p-2"
            loading="lazy"
            decoding="async"
          />
        )}
      </div>

      {!compact && (
        <p className="mt-3 text-center text-sm font-semibold text-foreground">
          {label}
        </p>
      )}
    </div>
  );
}
